/**
 * The sound effect registry. Every sound is synthesized in the browser (WebAudio, no samples) by a
 * function registered under an id, in a file of its own family under `sfx/sounds/` that calls
 * `registerSfx` when it loads (`sfx/index.ts` loads them all).
 *
 * Adding a sound is one call in the family's file:
 *
 *   registerSfx('combat.parry', { label: 'ปัดป้อง', cat: 'combat' }, (k, o) => {
 *     k.metal({ f: 1700, dur: 0.4, vol: 0.12 });
 *   });
 *
 * `k` (sfx/kit.ts) is the toolkit the sound draws with: `k.ctx` and `k.out` are the audio context
 * and the voice's bus, and its helpers (tones, filtered noise, ringing metal, plucked strings…)
 * schedule nodes that the engine counts, pans and cleans up. `o` holds the call's options.
 *
 * This module is plain logic (no WebAudio at load time), so tests can read it in Node.
 */
import type { SfxKit } from './kit';

/** Families of sounds, each with its own polyphony cap and mix level. */
export type SfxCategory = 'combat' | 'skill' | 'move' | 'ui' | 'world' | 'ambient' | 'econ' | 'social';

export const SFX_CATEGORIES: readonly SfxCategory[] = [
  'combat',
  'skill',
  'move',
  'ui',
  'world',
  'ambient',
  'econ',
  'social',
];

/** Thai names of the categories (the review page and the studio). */
export const SFX_CATEGORY_LABELS: Readonly<Record<SfxCategory, string>> = {
  combat: 'การต่อสู้',
  skill: 'วิชาและธาตุ',
  move: 'การเคลื่อนไหว · วิชาตัวเบา',
  ui: 'หน้าจอ',
  world: 'โลก',
  ambient: 'บรรยากาศ',
  econ: 'ของและเงินตรา',
  social: 'สังคม',
};

/**
 * Voices that may sound at once in each category. A new sound past the cap takes the place of the
 * oldest one (which fades out in a few milliseconds), so an eight-hit move or a crowd never piles up
 * nodes.
 */
export const SFX_POLYPHONY: Readonly<Record<SfxCategory, number>> = {
  combat: 10,
  skill: 8,
  move: 6,
  ui: 4,
  world: 6,
  ambient: 8,
  econ: 4,
  social: 5,
};

/** At most this many voices of one id at once, and of everything together. */
export const SFX_PER_ID = 4;
export const SFX_TOTAL = 40;

/** Mix level of each category under the master bus. */
export const SFX_CATEGORY_GAIN: Readonly<Record<SfxCategory, number>> = {
  combat: 1.4,
  skill: 1.35,
  move: 1.4,
  ui: 1.05,
  world: 1,
  ambient: 1.1,
  econ: 1.35,
  social: 1.25,
};

/** Options of one play call. */
export interface SfxOpts {
  /** Strength 0–1.5 (volume, and a brighter or longer sound where the sound uses it). Default 1. */
  intensity?: number;
  /** Seeds the pitch variation and any randomness inside the sound (default: a new seed each call). */
  seed?: number;
  /** Extra pitch multiplier on top of the variation. */
  pitch?: number;
  /** Stereo position −1 (left) … 1 (right). */
  pan?: number;
  /** Distance from the listener in tiles (world sounds): quieter with distance, silent far away. */
  dist?: number;
  /** Seconds from now before it sounds. */
  delay?: number;
  /** A length the sound should fill (a wind-up's charge time), seconds. */
  len?: number;
}

/** What a sound function gets: the options with the defaults filled in. */
export interface SfxCall {
  intensity: number;
  len: number | undefined;
}

export type SfxPlay = (k: SfxKit, o: SfxCall) => void;

export interface SfxInfo {
  /** Thai name (the review page, the studio). */
  label: string;
  cat: SfxCategory;
  /** Pitch variation per call, as a fraction (0.04 = ±4 %). */
  vary?: number;
  /** A fixed transposition of the whole sound (1 = as written): one recipe in another register. */
  pitch?: number;
  /** Level of this sound (1 = as written). */
  gain?: number;
  /** Repeats of this id closer together than this (seconds) are dropped. */
  gap?: number;
  /** Sounds until stopped (its handle's `stop`). */
  loop?: boolean;
  /** Seconds to render in the review export (default: worked out from the sound's own length). */
  preview?: number;
}

export interface SfxDef extends SfxInfo {
  readonly id: string;
  readonly play: SfxPlay;
}

const REGISTRY = new Map<string, SfxDef>();

/** Registers a sound under an id (once). */
export function registerSfx(id: string, info: SfxInfo, play: SfxPlay): void {
  if (REGISTRY.has(id)) throw new Error(`sfx '${id}' registered twice`);
  REGISTRY.set(id, { ...info, id, play });
}

export const getSfx = (id: string): SfxDef | undefined => REGISTRY.get(id);
export const hasSfx = (id: string): boolean => REGISTRY.has(id);
/** Every registered sound, in registration order. */
export const allSfx = (): SfxDef[] => [...REGISTRY.values()];

// ───────── voice pool ─────────

interface Slot {
  key: number;
  cat: SfxCategory;
  id: string;
  at: number;
  loop: boolean;
}

export type Admit = { ok: false } | { ok: true; key: number; steal: number[] };

/**
 * Who may sound: caps per category, per id and in all, with the oldest voice giving way to a new
 * one. Pure bookkeeping (times are the audio clock's), so it is tested without WebAudio.
 */
export class VoicePool {
  private live: Slot[] = [];
  private next = 1;

  constructor(
    private readonly caps: Readonly<Record<SfxCategory, number>> = SFX_POLYPHONY,
    private readonly perId = SFX_PER_ID,
    private readonly total = SFX_TOTAL,
  ) {}

  /**
   * Asks for a voice. Returns the voices to cut short to make room (already released here), or
   * `ok: false` when every voice that could give way is a loop.
   */
  admit(cat: SfxCategory, id: string, now: number, loop = false): Admit {
    const steal: number[] = [];
    const evict = (pred: (s: Slot) => boolean, cap: number): boolean => {
      let n = this.live.filter(pred).length;
      while (n >= cap) {
        // The oldest that is not a loop gives way.
        const victim = this.live.filter((s) => pred(s) && !s.loop).sort((a, b) => a.at - b.at)[0];
        if (!victim) return false;
        this.release(victim.key);
        steal.push(victim.key);
        n--;
      }
      return true;
    };
    if (!evict((s) => s.id === id, this.perId)) return { ok: false };
    if (!evict((s) => s.cat === cat, this.caps[cat])) return { ok: false };
    if (!evict(() => true, this.total)) return { ok: false };
    const key = this.next++;
    this.live.push({ key, cat, id, at: now, loop });
    return { ok: true, key, steal };
  }

  release(key: number): void {
    const i = this.live.findIndex((s) => s.key === key);
    if (i >= 0) this.live.splice(i, 1);
  }

  count(cat?: SfxCategory): number {
    return cat ? this.live.filter((s) => s.cat === cat).length : this.live.length;
  }

  countId(id: string): number {
    return this.live.filter((s) => s.id === id).length;
  }
}
